import { Trans } from '@lingui/react/macro';
import { type ReactNode } from 'react';

import type { IntegrationProviderCategory } from '@/api/integration';
import useDenyConnectionCreation from '@/app/integrations/useCanCreateConnection';
import { LinkButton } from '@/components/buttons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { Badge, Text, Title } from '@/components/typography';
import { ROUTES } from '@/router';

import IntegrationCategoryTag from './IntegrationCategoryTag';
import IntegrationStatus from './IntegrationStatus';

type Props = {
  id: string;
  name: string;
  description: string | null;
  category: IntegrationProviderCategory;
  icon: ReactNode;
  status: 'active' | 'notConnected' | 'broken';
  available: boolean;
};

const IntegrationCard = ({ id, name, description, category, icon, status, available }: Props) => {
  const denyConnectionCreation = useDenyConnectionCreation(category) && status === 'notConnected';

  return (
    <div className="flex w-full flex-col gap-8 rounded-md border border-gray-300 p-4">
      <div className="flex items-start justify-between">
        <div className="flex h-16 w-25 items-center">{icon}</div>
        <IntegrationStatus status={status} />
      </div>
      <div className="flex flex-1 flex-col items-start gap-4">
        <div className="flex flex-1 flex-col gap-1">
          <Title level={3} weight="semi-bold">
            {name}
          </Title>
          <Text variant="body2">{description}</Text>
        </div>
        <div className="flex w-full items-center justify-between">
          {available ? (
            <Tooltip open={denyConnectionCreation ? undefined : false}>
              <TooltipTrigger
                render={
                  <LinkButton
                    to={ROUTES.integrationById(id) ?? ROUTES.integrationByCategory()}
                    disabled={denyConnectionCreation}
                  >
                    {status === 'notConnected' ? <Trans>Connect</Trans> : <Trans>Details</Trans>}
                  </LinkButton>
                }
              />
              <TooltipContent>
                <Trans>
                  You have already connected a CRM account. Only one CRM can be connected to your
                  instance.
                </Trans>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Badge size="large" color="info">
              <Trans>Coming soon</Trans>
            </Badge>
          )}
          <IntegrationCategoryTag category={category} />
        </div>
      </div>
    </div>
  );
};

export default IntegrationCard;
